/**
 * /admin/pipeline — operators: where every video is in the pipeline, with the
 * uploader's email, the step its job is on and that step's counters
 * (GET /ops/pipeline, refreshed every 15 s). Not under /ops: that prefix is
 * the API's. A row opens /admin/pipeline/:id.
 */
import { useCallback, useState } from "react";
import { Link, useNavigate } from "react-router";
import { get } from "../api/client";
import { useLoad, usePoll } from "../api/hooks";
import type { PipelineItem } from "../api/types";
import { useAuth } from "../auth/AuthContext";
import { Shell } from "../components/Shell";
import { Bar, Dot, ErrorLine, Seg } from "../components/ui";
import { fmtDuration } from "../lib/format";
import { STALE_S, ago, counterShare, counterText, partLabel, secondsSince, whereIs } from "../lib/pipeline";

type Scope = "active" | "all";

export default function AdminPipeline() {
  const { me } = useAuth();
  const [scope, setScope] = useState<Scope>("active");
  const list = useLoad(() => get<{ items: PipelineItem[]; generated_at: string }>(`/ops/pipeline?scope=${scope}`), [scope]);
  const reload = useCallback(() => list.reload(), [list.reload]);
  usePoll(reload, 15_000, !!me?.user.is_operator);
  if (me && !me.user.is_operator) return <OperatorsOnly />;
  const items = list.data?.items ?? [];
  const running = items.filter((i) => i.job?.state === "running").length;
  const waiting = items.filter((i) => i.job && (i.job.state === "created" || i.job.state === "dispatched")).length;
  const failed = items.filter((i) => ["failed", "ingest_failed", "unusable"].includes(i.video.state)).length;
  return (
    <Shell back="/" title="Pipeline" right={<Link to="/admin/beta-submissions">Beta submissions</Link>}>
      <div className="p pipe" style={{ paddingTop: 24, paddingBottom: 48 }} data-testid="admin-pipeline">
        <div className="row between wrap-r" style={{ rowGap: 12 }}>
          <div>
            <p className="cap">Operators</p>
            <h1 className="mt6">Pipeline</h1>
          </div>
          <div style={{ width: 260 }}>
            <Seg value={scope} onChange={setScope} options={[{ value: "active", label: "In progress" }, { value: "all", label: "All" }]} />
          </div>
        </div>
        <p className="sub mt10">
          {running} running · {waiting} waiting · {failed} failed{scope === "active" ? " (active, and finished in the last 24 h)" : ""}. Refreshes every 15 s.
        </p>
        <ErrorLine error={list.error} />
        {list.data && items.length === 0 && <p className="tiny mt24">No videos here.</p>}

        {/* desktop: one table row per video */}
        <div className="pipe-d mt24">
        <table className="tbl pipe-tbl" data-testid="pipeline-table">
          <thead>
            <tr><th style={{ width: "22%" }}>Video</th><th style={{ width: "21%" }}>Uploaded by</th><th style={{ width: "17%" }}>Where</th><th style={{ width: "26%" }}>Progress</th><th style={{ width: "14%" }}>Updated</th></tr>
          </thead>
          <tbody>
            {items.map((it) => <DesktopRow key={it.video.id} it={it} />)}
          </tbody>
        </table>
        </div>

        {/* phone: one card per video */}
        <div className="pipe-m mt16">
          {items.map((it) => <PhoneCard key={it.video.id} it={it} />)}
        </div>
      </div>
    </Shell>
  );
}

function rowView(it: PipelineItem) {
  const where = whereIs(it.video.state, it.job, it.progress, it.attempt?.state);
  const live = it.job?.state === "running";
  const p = live ? it.progress : null;
  const share = counterShare(p?.progress);
  const updated = it.attempt?.updated_at ?? it.video.updated_at;
  const stale = live && secondsSince(it.attempt?.worker_seen_at ?? updated) > STALE_S;
  return { where, p, share, updated, stale };
}

function DesktopRow({ it }: { it: PipelineItem }) {
  const nav = useNavigate();
  const { where, p, share, updated, stale } = rowView(it);
  return (
    <tr className="pipe-row" onClick={() => nav(`/admin/pipeline/${it.video.id}`)} data-testid="pipeline-row">
      <td>
        <Link to={`/admin/pipeline/${it.video.id}`} className="med" onClick={(e) => e.stopPropagation()}>{it.video.title}</Link>
        <div className="tiny mt6">{it.video.id}{it.video.duration_ms ? ` · ${fmtDuration(it.video.duration_ms)}` : ""}</div>
      </td>
      <td>
        <div>{it.uploader.email}</div>
        <div className="tiny mt6">{it.workspace.plan}{it.uploader.via === "workspace_owner" ? " · workspace owner" : ""}</div>
      </td>
      <td>
        <div><Dot kind={where.dot} />{where.label}</div>
        {where.detail && <div className="tiny mt6">{where.detail}</div>}
      </td>
      <td>
        {p?.progress ? (
          <>
            <div className="num">{counterText(p.progress)}</div>
            {share != null && <div className="mt6"><Bar value={share} /></div>}
            <div className="tiny mt6">{[partLabel(p.part), p.secondary ? counterText(p.secondary) : ""].filter(Boolean).join(" · ")}</div>
          </>
        ) : it.video.error ? (
          <div className="tiny bad">{it.video.error.code}{it.video.error.message ? `: ${it.video.error.message.slice(0, 90)}` : ""}</div>
        ) : null}
      </td>
      <td>
        <div className={stale ? "bad" : ""}>{ago(updated)}</div>
        {it.attempt?.pod_id && <div className="tiny mt6">{it.attempt.pod_id}</div>}
      </td>
    </tr>
  );
}

function PhoneCard({ it }: { it: PipelineItem }) {
  const { where, p, share, updated, stale } = rowView(it);
  return (
    <Link to={`/admin/pipeline/${it.video.id}`} className="pipe-card" data-testid="pipeline-card">
      <div className="row between">
        <span className="med" style={{ minWidth: 0, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{it.video.title}</span>
        <span className={`tiny ${stale ? "bad" : ""}`} style={{ flex: "none" }}>{ago(updated)}</span>
      </div>
      <div className="tiny mt6">{it.uploader.email}</div>
      <div className="mt10"><Dot kind={where.dot} />{where.label}{where.detail ? <span className="tiny"> · {where.detail}</span> : null}</div>
      {p?.progress && (
        <>
          <div className="sub mt6">{counterText(p.progress)}{p.part ? ` · ${partLabel(p.part)}` : ""}</div>
          {share != null && <div className="mt6"><Bar value={share} /></div>}
        </>
      )}
    </Link>
  );
}

export function OperatorsOnly({ shows = "every user's videos" }: { shows?: string }) {
  return (
    <Shell back="/">
      <div className="p" style={{ paddingTop: 24 }}>
        <h1>Operators only</h1>
        <p className="sub mt10">This page shows {shows}. Your account is not an operator.</p>
      </div>
    </Shell>
  );
}
